import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox, Loader2, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { InstallationCombustionFuelSelect } from '@/features/installations/components/installation-combustion-fuel-select'
import { InstallationDeleteConfirmDialog } from '@/features/installations/components/installation-delete-confirm-dialog'
import { useInstallationDeleteConfirm } from '@/features/installations/hooks/use-installation-delete-confirm'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { usePermissions } from '@/shared/hooks/usePermissions'
import {
  useInstallationFuelMutations,
  useInstallationFuelsQuery,
  useOperatorConfig,
} from '@/features/installations/hooks/useInstallationConfiguration'

export function InstallationFuelsPage() {
  const { t } = useTranslation('installations')
  const { checkPermission } = usePermissions()
  const canEdit = checkPermission('installation', 'update')

  const listQuery = useInstallationFuelsQuery()
  const operatorConfigQuery = useOperatorConfig()
  const mutations = useInstallationFuelMutations()
  const deleteConfirm = useInstallationDeleteConfirm()

  const [createOpen, setCreateOpen] = useState(false)
  const [selectedFuelTypeId, setSelectedFuelTypeId] = useState<string>('')

  const rows = listQuery.data
  const combustionFuelTypes = operatorConfigQuery.data?.combustionFuelTypes ?? null
  const operatorConfigFailed = operatorConfigQuery.isError

  const fuelOptions = useMemo(() => {
    if (operatorConfigFailed) {
      return []
    }
    const list = combustionFuelTypes ?? []
    const selectedIds = new Set(rows?.map((row) => row.fuelTypeId) ?? [])
    return list
      .filter((fuelType) => !selectedIds.has(fuelType.id))
      .sort((first, second) => first.name.localeCompare(second.name))
  }, [combustionFuelTypes, operatorConfigFailed, rows])

  const fuelLabelById = useMemo(() => {
    const map = new Map<string, string>()
    const list = combustionFuelTypes ?? []
    for (const fuelType of list) {
      map.set(fuelType.id, fuelType.name)
    }
    return map
  }, [combustionFuelTypes])

  const resetCreateForm = () => {
    setSelectedFuelTypeId('')
  }

  const submitFuel = async () => {
    if (!selectedFuelTypeId) {
      return
    }
    await mutations.addFuel.mutateAsync(selectedFuelTypeId)
    resetCreateForm()
    setCreateOpen(false)
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.pendingDelete) {
      return
    }
    await mutations.removeFuel.mutateAsync(deleteConfirm.pendingDelete.id)
    deleteConfirm.clearPendingDelete()
  }

  if (listQuery.isLoading || operatorConfigQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (rows === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">{t('configuration.loadError')}</p>
      </div>
    )
  }

  if (operatorConfigFailed) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">{t('configuration.operatorConfigLoadError')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('configuration.fuels.title')}
          </h1>
          <p className="text-muted-foreground">{t('configuration.fuels.description')}</p>
        </div>
        {canEdit && rows.length > 0 && fuelOptions.length > 0 && (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            {t('common:actions.create')}
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16">
          <Inbox className="size-10 text-muted-foreground/50" />
          <div className="space-y-1 text-center">
            <h3 className="text-lg font-semibold">{t('configuration.fuels.emptyTitle')}</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('configuration.fuels.emptyDescription')}
            </p>
            {canEdit && fuelOptions.length === 0 && (
              <p className="pt-2 text-sm text-muted-foreground">
                {t('configuration.fuels.noCatalogFuels')}
              </p>
            )}
          </div>
          {canEdit && fuelOptions.length > 0 && (
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('common:actions.create')}
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="group/row">
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[85%] whitespace-normal'
                  )}
                >
                  {t('configuration.table.fuelType')}
                </TableHead>
                {canEdit && (
                  <TableHead
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'w-[88px] min-w-0 max-w-[88px] text-end'
                    )}
                  >
                    {t('configuration.table.actions')}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[85%] whitespace-normal'
                    )}
                  >
                    {fuelLabelById.get(row.fuelTypeId) ?? row.fuelTypeId}
                  </TableCell>
                  {canEdit && (
                    <TableCell
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        'w-[88px] min-w-0 max-w-[88px] text-end'
                      )}
                    >
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-destructive"
                        aria-label={t('configuration.a11y.deleteFuel', {
                          label: fuelLabelById.get(row.fuelTypeId) ?? row.fuelTypeId,
                        })}
                        onClick={() =>
                          deleteConfirm.requestDelete({
                            id: row.id,
                            label: fuelLabelById.get(row.fuelTypeId) ?? row.fuelTypeId,
                          })
                        }
                        disabled={mutations.removeFuel.isPending}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) {
            resetCreateForm()
          }
        }}
      >
        <DialogContent className="flex h-[min(24rem,90vh)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
          <DialogHeader className="gap-1 border-b border-border px-6 py-4 text-start">
            <DialogTitle>{t('configuration.dialog.addFuel')}</DialogTitle>
          </DialogHeader>
          <InstallationCombustionFuelSelect
            active={createOpen}
            value={selectedFuelTypeId}
            onValueChange={setSelectedFuelTypeId}
            options={fuelOptions}
            listAriaLabel={t('configuration.fuels.selectPlaceholder')}
          />
          <DialogFooter className="gap-2 border-t border-border px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateOpen(false)
                resetCreateForm()
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!selectedFuelTypeId || mutations.addFuel.isPending}
              onClick={submitFuel}
            >
              {t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InstallationDeleteConfirmDialog
        pendingDelete={deleteConfirm.pendingDelete}
        open={deleteConfirm.isConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteConfirm.clearPendingDelete()
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={mutations.removeFuel.isPending}
      />
    </div>
  )
}
